import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useEffect } from "react";

export const CATEGORIAS_HINOS = [
  { value: "avulsos", label: "Hinos Avulsos" },
  { value: "com_cifra", label: "Hinos com Cifra" },
  { value: "iniciantes", label: "Hinos Fáceis para Iniciantes" },
  { value: "mais_acessados", label: "Hinos Mais Acessados" },
] as const;

export type CategoriaHino = typeof CATEGORIAS_HINOS[number]["value"];

export interface Hino {
  id: string;
  numero: number;
  titulo: string;
  letra: string;
  cifra: string;
  video_url: string;
  audio_url: string;
  cover_image_url: string;
  categoria: CategoriaHino;
  visualizacoes: number;
  created_at: string;
  updated_at: string;
}

export interface HinoInput {
  numero: number;
  titulo: string;
  letra: string;
  cifra: string;
  video_url?: string;
  audio_url?: string;
  cover_image_url?: string;
  categoria?: CategoriaHino;
}

const STORAGE_KEY = "louve_hinos_cache";

// Save hinos to localStorage for offline access
const saveToLocalStorage = (hinos: Hino[]) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(hinos));
  } catch (e) {
    console.warn("Could not save hinos to localStorage:", e);
  }
};

// Load hinos from localStorage
const loadFromLocalStorage = (): Hino[] | null => {
  try {
    const cached = localStorage.getItem(STORAGE_KEY);
    if (cached) {
      return JSON.parse(cached) as Hino[];
    }
  } catch (e) {
    console.warn("Could not load hinos from localStorage:", e);
  }
  return null;
};

// Check if we're online
const isOnline = () => typeof navigator !== "undefined" && navigator.onLine;

export const useHinos = () => {
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ["hinos"],
    queryFn: async () => {
      // If offline, try to load from localStorage first
      if (!isOnline()) {
        const cached = loadFromLocalStorage();
        if (cached) {
          return cached;
        }
        throw new Error("Você está offline e não há dados em cache.");
      }

      // Online: fetch from Supabase
      const { data, error } = await supabase
        .from("hinos")
        .select("*")
        .order("numero", { ascending: true });

      if (error) {
        // If fetch fails, try localStorage as fallback
        const cached = loadFromLocalStorage();
        if (cached) {
          return cached;
        }
        throw error;
      }

      // Save to localStorage for offline access
      saveToLocalStorage(data as Hino[]);
      return data as Hino[];
    },
    staleTime: 1000 * 10, // 10 seconds
    gcTime: 1000 * 60 * 60 * 24,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    refetchInterval: 1000 * 60, // Refetch every 60 seconds
  });

  // Listen for online/offline events to refresh data
  useEffect(() => {
    const handleOnline = () => {
      queryClient.invalidateQueries({ queryKey: ["hinos"] });
    };
    window.addEventListener("online", handleOnline);

    return () => {
      window.removeEventListener("online", handleOnline);
    };
  }, [queryClient]);

  return query;
};

export const useHino = (id: string | undefined) => {
  return useQuery({
    queryKey: ["hino", id],
    queryFn: async () => {
      if (!id) return null;

      // First try localStorage (fast, works offline)
      const cached = loadFromLocalStorage();
      const cachedHino = cached?.find((h) => h.id === id);

      if (!isOnline()) {
        if (cachedHino) {
          return cachedHino;
        }
        throw new Error("Você está offline e este hino não está em cache.");
      }

      // Online: fetch fresh data
      const { data, error } = await supabase
        .from("hinos")
        .select("*")
        .eq("id", id)
        .maybeSingle();

      if (error) {
        if (cachedHino) return cachedHino;
        throw error;
      }

      return data as Hino | null;
    },
    enabled: !!id,
    staleTime: 1000 * 30, // 30 seconds
    refetchOnWindowFocus: true,
  });
};

export const useHinoByNumero = (numero: number | undefined) => {
  return useQuery({
    queryKey: ["hino-numero", numero],
    queryFn: async () => {
      if (!numero) return null;
      
      const { data, error } = await supabase
        .from("hinos")
        .select("*")
        .eq("numero", numero)
        .maybeSingle();

      if (error) throw error;
      return data as Hino | null;
    },
    enabled: !!numero,
  });
};

export const useCreateHino = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (hino: HinoInput) => {
      const { data, error } = await supabase
        .from("hinos")
        .insert(hino)
        .select()
        .single();

      if (error) throw error;
      return data as Hino;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["hinos"] });
    },
  });
};

export const useUpdateHino = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, ...hino }: { id: string } & Partial<HinoInput>) => {
      const { data, error } = await supabase
        .from("hinos")
        .update(hino)
        .eq("id", id)
        .select()
        .single();

      if (error) throw error;
      return data as Hino;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["hinos"] });
      queryClient.invalidateQueries({ queryKey: ["hino", data.id] });
    },
  });
};

export const useDeleteHino = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from("hinos")
        .delete()
        .eq("id", id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["hinos"] });
    },
  });
};
